// useSavedPosts.jsx
// Context + provider that tracks the current user's saved listings.
// Load once on auth, write back with arrayUnion / arrayRemove.
// All SaveButton instances share this single context — one Firestore read per session.
//
// Saved lists live in userPrivate/{uid} (readable only by their owner), NOT on
// the public users doc. Accounts from before that move get migrated here on
// first load; the nightly rollup migrates everyone else.

import React, { createContext, useContext, useEffect, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  arrayRemove,
  arrayUnion,
  deleteField,
  doc,
  getDoc,
  setDoc,
  updateDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import { toast } from "react-toastify";

const SavedPostsContext = createContext({
  savedIds: new Set(),
  toggleSave: () => {},
  uid: null,
});

export function SavedPostsProvider({ children }) {
  const [uid, setUid] = useState(null);
  const [savedIds, setSavedIds] = useState(new Set());

  // Mirror auth state
  useEffect(() => {
    const auth = getAuth();
    const unsub = onAuthStateChanged(auth, (user) => {
      setUid(user?.uid ?? null);
      if (!user) setSavedIds(new Set());
    });
    return unsub;
  }, []);

  // Load saved listings when the user logs in, migrating any list still on the
  // public users doc into userPrivate.
  useEffect(() => {
    if (!uid) return;
    (async () => {
      const privRef = doc(db, "userPrivate", uid);
      const priv = await getDoc(privRef);
      let ids = priv.data()?.savedPost ?? [];
      const legacy = (await getDoc(doc(db, "users", uid))).data()?.savedPost;
      if (legacy !== undefined) {
        if (legacy.length) {
          await setDoc(privRef, { savedPost: arrayUnion(...legacy) }, { merge: true });
          ids = [...new Set([...ids, ...legacy])];
        }
        await updateDoc(doc(db, "users", uid), { savedPost: deleteField() });
      }
      setSavedIds(new Set(ids));
    })().catch((err) => console.error("Failed to load saved posts:", err));
  }, [uid]);

  async function toggleSave(postId) {
    if (!uid) {
      toast.info("Sign in to save listings.");
      return;
    }
    const wasSaved = savedIds.has(postId);

    // Optimistic update
    setSavedIds((prev) => {
      const next = new Set(prev);
      wasSaved ? next.delete(postId) : next.add(postId);
      return next;
    });

    try {
      await setDoc(
        doc(db, "userPrivate", uid),
        { savedPost: wasSaved ? arrayRemove(postId) : arrayUnion(postId) },
        { merge: true }
      );
    } catch (err) {
      // Rollback
      setSavedIds((prev) => {
        const next = new Set(prev);
        wasSaved ? next.add(postId) : next.delete(postId);
        return next;
      });
      console.error(err);
      toast.error("Failed to update saved listings.");
    }
  }

  return (
    <SavedPostsContext.Provider value={{ savedIds, toggleSave, uid }}>
      {children}
    </SavedPostsContext.Provider>
  );
}

export function useSavedPosts() {
  return useContext(SavedPostsContext);
}
